import { FC, useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { User, LogOut, Menu, ChevronDown } from 'lucide-react';
import { useSelectedUser } from '@/contexts/SelectedUserContext';
import { useEditMode } from '@/hooks/useEditMode';
import { useUser } from '@/hooks';
import { PlatformStatusIndicator, PlatformStatus } from '@/components/platform/PlatformStatusIndicator';
import api from '@/utils/api';

interface HeaderProps {
  onLogout: () => void | Promise<void>;
  userName?: string;
  onMenuToggle: () => void;
}

// Header component implementation
export const Header: FC<HeaderProps> = ({ onLogout, userName, onMenuToggle }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { selectedUser } = useSelectedUser();
  const { isEditMode } = useEditMode();
  const currentUser = useUser();

  // Platform connection statuses (only for external users)
  interface PlatformStatuses {
    linkedin?: PlatformStatus | null;
    facebook?: PlatformStatus | null;
    instagram?: PlatformStatus | null;
    twitter?: PlatformStatus | null;
  }
  const [platformStatuses, setPlatformStatuses] = useState<PlatformStatuses | null>(null);

  const fetchPlatformStatuses = useCallback(() => {
    if (currentUser?.role === 'external') {
      api.get('/users/connection-status')
        .then(({ data }) => setPlatformStatuses(data))
        .catch(err => console.error('Failed to fetch platform statuses:', err));
    }
  }, [currentUser]);

  useEffect(() => {
    fetchPlatformStatuses();
  }, [fetchPlatformStatuses]);

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="sticky top-0 bg-white border-b border-gray-200 z-[999]">
      <div className="flex justify-between items-center px-6 lg:px-8 h-16">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          {/* Mobile Menu Toggle */}
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded transition-colors"
            aria-label="Toggle navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Status Indicators - Combined - Only show for internal/admin users */}
          {currentUser?.role !== 'external' && (
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded border border-gray-200">
              {/* Selected User */}
              {selectedUser && (
                <>
                  <User className="w-3 h-3 text-gray-500" />
                  <span className="text-xs text-gray-600">{selectedUser.name}</span>
                  <div className="w-px h-3 bg-gray-300 mx-1"></div>
                </>
              )}

              {/* Edit Mode */}
              <div className={`w-1.5 h-1.5 rounded-full ${isEditMode ? 'bg-orange-500' : 'bg-green-500'}`}></div>
              <span className="text-xs text-gray-600">{isEditMode ? 'Edit' : 'View'}</span>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Platform Connection Indicators - Only for external users */}
          {currentUser?.role === 'external' && platformStatuses && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              <PlatformStatusIndicator
                platform="linkedin"
                status={platformStatuses.linkedin || { connected: false }}
                compact
              />
              <PlatformStatusIndicator
                platform="facebook"
                status={platformStatuses.facebook || { connected: false }}
                compact
              />
              <PlatformStatusIndicator
                platform="instagram"
                status={platformStatuses.instagram || { connected: false }}
                compact
              />
              <PlatformStatusIndicator
                platform="twitter"
                status={platformStatuses.twitter || { connected: false }}
                compact
              />
            </div>
          )}

          {/* User Menu Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition-colors"
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center text-white text-xs font-medium">
                {userName ? getInitials(userName) : <User className="w-4 h-4" />}
              </div>
              <span className="text-sm font-medium text-gray-700 hidden sm:block">{userName}</span>
              <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />

                {/* Menu Content */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 shadow-lg rounded-lg py-1 z-50">
                  <NavLink
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    <span>Account Settings</span>
                  </NavLink>

                  <div className="border-t border-gray-100 my-1"></div>

                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

